import assertNever from 'assert-never';

import {
	FirmwareUpdateRequestError,
	FirmwareUpdateRequestStage,
	FirmwareUpdateRequestStatus,
	InventoryDevice
} from '@shure/cloud/shared/models/devices';

import {
	FirmwareUpdateExecutionStage as SysApiFirmwareUpdateExecutionStage,
	FirmwareUpdateRequestStatus as SysApiFirmwareUpdateRequestStatus,
	FirmwareUpdateRequestContentFragment
} from '../graphql/generated/cloud-sys-api';

export function mapSysApiFirmwareUpdateRequestToFeature(
	supported: boolean,
	currentRequest: FirmwareUpdateRequestContentFragment | null | undefined
): InventoryDevice['features']['primaryFirmwareUpdateRequest'] | undefined {
	if (supported === false || currentRequest === null || currentRequest === undefined) {
		return undefined;
	}

	return {
		currentRequest: {
			progress: currentRequest.progress,
			package: currentRequest.packageKey,
			version: currentRequest.packageVersion,
			status: mapSysApiFirmwareUpdateRequestStatus(currentRequest.status),
			executingStage: mapSysApiFirmwareUpdateExecutionStage(currentRequest.executionStatus?.stage),
			error: mapSysApiFirmwareUpdateRequestError(currentRequest.error)
		},
		isMissing: false
	};
}

export function mapSysApiFirmwareUpdateRequestStatus(
	value: SysApiFirmwareUpdateRequestStatus
): FirmwareUpdateRequestStatus {
	switch (value) {
		case SysApiFirmwareUpdateRequestStatus.Pending:
			return FirmwareUpdateRequestStatus.Pending;
		case SysApiFirmwareUpdateRequestStatus.Executing:
			return FirmwareUpdateRequestStatus.Executing;
		case SysApiFirmwareUpdateRequestStatus.Complete:
			return FirmwareUpdateRequestStatus.Complete;
		case SysApiFirmwareUpdateRequestStatus.Errored:
			return FirmwareUpdateRequestStatus.Errored;
		case SysApiFirmwareUpdateRequestStatus.Canceled:
			return FirmwareUpdateRequestStatus.Canceled;
		default:
			return assertNever(value);
	}
}

export function mapSysApiFirmwareUpdateExecutionStage(
	value: SysApiFirmwareUpdateExecutionStage | null | undefined
): FirmwareUpdateRequestStage | undefined {
	if (value === null || value === undefined) {
		return undefined;
	}

	switch (value) {
		case SysApiFirmwareUpdateExecutionStage.ReservingDevice:
			return FirmwareUpdateRequestStage.ReservingDevice;
		case SysApiFirmwareUpdateExecutionStage.PreInstallValidating:
			return FirmwareUpdateRequestStage.PreInstallValidating;
		case SysApiFirmwareUpdateExecutionStage.TransferringFiles:
			return FirmwareUpdateRequestStage.TransferringFiles;
		case SysApiFirmwareUpdateExecutionStage.Installing:
			return FirmwareUpdateRequestStage.Installing;
		case SysApiFirmwareUpdateExecutionStage.Rebooting:
			return FirmwareUpdateRequestStage.Rebooting;
		case SysApiFirmwareUpdateExecutionStage.Verifying:
			return FirmwareUpdateRequestStage.Verifying;
		default:
			return assertNever(value);
	}
}

// The Graphql spec doesn't define an enum for all the possible errors so
// that adding values in the future isn't a breaking change. Below is the
// current set of values packages as a const object.
// eslint-disable-next-line @typescript-eslint/naming-convention
const SysApiFirmwareUpdateRequestError = {
	INVALID_PACKAGE: 'INVALID_PACKAGE',
	CORRUPT_PACKAGE: 'CORRUPT_PACKAGE',
	CONNECTIVITY_LOSS: 'CONNECTIVITY_LOSS',
	DEVICE_BUSY: 'DEVICE_BUSY',
	DEVICE_NOT_FOUND: 'DEVICE_NOT_FOUND',
	FILE_TRANSFER_ERROR: 'FILE_TRANSFER_ERROR',
	DEVICE_REJECTED_PACKAGE: 'DEVICE_REJECTED_PACKAGE',
	INSTALLATION_ERROR: 'INSTALLATION_ERROR',
	VERIFICATION_ERROR: 'VERIFICATION_ERROR',
	QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
	MISSING_PROXY: 'MISSING_PROXY',
	NOT_CONNECTED_VIA_USB: 'NOT_CONNECTED_VIA_USB',
	DANTE_LOCKED: 'DANTE_LOCKED',
	DANTE_NETWORK_MISMATCH: 'DANTE_NETWORK_MISMATCH',
	NETWORK_LOCKED: 'NETWORK_LOCKED',
	UPDATE_IN_PROGRESS: 'UPDATE_IN_PROGRESS',
	NOT_DOCKED: 'NOT_DOCKED'
} as const;

type SysApiFirmwareUpdateRequestError =
	(typeof SysApiFirmwareUpdateRequestError)[keyof typeof SysApiFirmwareUpdateRequestError];

export function mapSysApiFirmwareUpdateRequestError(
	value: string | null | undefined
): FirmwareUpdateRequestError | undefined {
	if (value === null || value === undefined || value === '') {
		return undefined;
	}

	switch (value) {
		case SysApiFirmwareUpdateRequestError.INVALID_PACKAGE:
			return FirmwareUpdateRequestError.InvalidPackage;
		case SysApiFirmwareUpdateRequestError.CORRUPT_PACKAGE:
			return FirmwareUpdateRequestError.CorruptPackage;
		case SysApiFirmwareUpdateRequestError.CONNECTIVITY_LOSS:
			return FirmwareUpdateRequestError.ConnectivityLoss;
		case SysApiFirmwareUpdateRequestError.DEVICE_BUSY:
			return FirmwareUpdateRequestError.DeviceBusy;
		case SysApiFirmwareUpdateRequestError.DEVICE_NOT_FOUND:
			return FirmwareUpdateRequestError.DeviceNotFound;
		case SysApiFirmwareUpdateRequestError.FILE_TRANSFER_ERROR:
			return FirmwareUpdateRequestError.FileTransferError;
		case SysApiFirmwareUpdateRequestError.DEVICE_REJECTED_PACKAGE:
			return FirmwareUpdateRequestError.DeviceRejectedPackage;
		case SysApiFirmwareUpdateRequestError.INSTALLATION_ERROR:
			return FirmwareUpdateRequestError.InstallationError;
		case SysApiFirmwareUpdateRequestError.VERIFICATION_ERROR:
			return FirmwareUpdateRequestError.VerificationError;
		case SysApiFirmwareUpdateRequestError.QUOTA_EXCEEDED:
			return FirmwareUpdateRequestError.QuotaExceeded;
		case SysApiFirmwareUpdateRequestError.MISSING_PROXY:
			return FirmwareUpdateRequestError.MissingProxy;
		case SysApiFirmwareUpdateRequestError.NOT_CONNECTED_VIA_USB:
			return FirmwareUpdateRequestError.NotConnectedViaUsb;
		case SysApiFirmwareUpdateRequestError.DANTE_LOCKED:
			return FirmwareUpdateRequestError.DanteLocked;
		case SysApiFirmwareUpdateRequestError.DANTE_NETWORK_MISMATCH:
			return FirmwareUpdateRequestError.DanteNetworkMismatch;
		case SysApiFirmwareUpdateRequestError.NETWORK_LOCKED:
			return FirmwareUpdateRequestError.NetworkLocked;
		case SysApiFirmwareUpdateRequestError.UPDATE_IN_PROGRESS:
			return FirmwareUpdateRequestError.UpdateInProgress;
		case SysApiFirmwareUpdateRequestError.NOT_DOCKED:
			return FirmwareUpdateRequestError.NotDocked;
		default:
			// Future server values land here safely.
			return FirmwareUpdateRequestError.Unknown;
	}
}
